import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TamaguiElement } from 'tamagui'
import { Trans } from '@lingui/react/macro'

import { Button } from './Button/Button'

import { Spinner } from './Spinner'

/**
 * Sentinel component that auto-loads more items when scrolled into view.
 * Uses IntersectionObserver to detect visibility and triggers `loadNext`.
 * Renders a Spinner while waiting for the next page.
 *
 * Two things this has to get right:
 *
 * 1. `loadNext` from Relay's `usePaginationFragment` is not referentially
 *    stable — it is rebuilt whenever the connection's data, cursor or fragment
 *    ref changes. Observing it as a dependency tore down and re-created the
 *    observer after every page, and re-observing a target always re-delivers
 *    its current intersection state, so it called `loadNext` again immediately
 *    with nothing to stop it.
 *
 * 2. IntersectionObserver only fires on *changes*, so with a stable observer a
 *    sentinel that stays visible after a page lands would never fire again.
 *    The latest intersection state is tracked in a ref and re-checked when a
 *    page finishes, which keeps loading while the sentinel is genuinely in
 *    view without ever having more than one request in flight.
 */
export const AutoLoadMore = ({
  hasNext,
  loadNext,
  count,
  isLoadingNext,
}: {
  hasNext: boolean
  loadNext: (
    count: number,
    options?: { onComplete?: (error: Error | null) => void }
  ) => void
  count: number
  isLoadingNext: boolean
}) => {
  const [sentinel, setSentinel] = useState<Element | null>(null)

  const [hasError, setHasError] = useState(false)
  const failedRef = useRef(false)
  const isLoadingNextRef = useRef(isLoadingNext)
  isLoadingNextRef.current = isLoadingNext

  const loadNextRef = useRef(loadNext)
  loadNextRef.current = loadNext

  const isIntersectingRef = useRef(false)

  // `isLoadingNext` only becomes true on the render after `loadNext` is called,
  // so the observer could otherwise fire several times before Relay reports the
  // request as in flight.
  const requestedRef = useRef(false)

  const countRef = useRef(count)
  countRef.current = count

  const hasNextRef = useRef(hasNext)
  hasNextRef.current = hasNext

  const requestPage = useCallback(() => {
    if (
      !hasNextRef.current ||
      requestedRef.current ||
      isLoadingNextRef.current
    ) {
      return
    }

    requestedRef.current = true
    loadNextRef.current(countRef.current, {
      onComplete: (error) => {
        // Latch the failure before Relay's loading=false render can retry.
        requestedRef.current = false
        failedRef.current = error != null
        setHasError(error != null)
      },
    })
  }, [])

  const maybeLoad = useCallback(() => {
    if (!isIntersectingRef.current || failedRef.current) return
    requestPage()
  }, [requestPage])

  // A finished page clears the guard, then re-checks: if the sentinel is still
  // on screen there is more to pull in.
  //
  // `loadNext` is included so the guard also clears when the connection
  // advances without `isLoadingNext` ever flipping. Relay's `loadNext` is a
  // no-op while the parent query is in flight, and without this the sentinel
  // would stay latched and never retry.
  useEffect(() => {
    if (isLoadingNext) return

    requestedRef.current = false
    maybeLoad()
  }, [isLoadingNext, loadNext, maybeLoad])

  const sentinelRef = useCallback((node: TamaguiElement | null) => {
    setSentinel((node as unknown as Element) ?? null)
  }, [])

  useEffect(() => {
    if (
      !sentinel ||
      typeof window === 'undefined' ||
      !window.IntersectionObserver
    ) {
      return
    }

    const observer = new window.IntersectionObserver((entries) => {
      isIntersectingRef.current = entries.some((entry) => entry.isIntersecting)
      maybeLoad()
    })

    observer.observe(sentinel)

    return () => {
      isIntersectingRef.current = false
      observer.disconnect()
    }
  }, [sentinel, maybeLoad])

  if (!hasNext) return null

  if (hasError) {
    return (
      <Button
        secondary
        onPress={() => {
          failedRef.current = false
          setHasError(false)
          requestPage()
        }}
      >
        <Button.Text secondary>
          <Trans>Retry</Trans>
        </Button.Text>
      </Button>
    )
  }

  return (
    <Spinner
      ref={sentinelRef as React.Ref<any>}
      size='$9'
      color='$primaryColor'
    />
  )
}
